import { Button, Loading, notify, useConfirmModal } from '@affine/component';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { useBackendAvailable } from '@affine/core/components/hooks/use-backend-availability';
import { useDocMetaHelper } from '@affine/core/components/hooks/use-block-suite-page-meta';
import { WorkspaceServerService } from '@affine/core/modules/cloud';
import { OrganizeService } from '@affine/core/modules/organize';
import {
  type Member,
  WorkspaceMembersService,
} from '@affine/core/modules/permissions';
import { WorkspaceQuotaService } from '@affine/core/modules/quota';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { UserFriendlyError } from '@affine/error';
import {
  getUserDocGrantsQuery,
  getUserFolderGrantsQuery,
  Permission,
} from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  SettingsOfflineHeaderAction,
  useSettingsOfflineIncompleteLabel,
} from '../../shared/settings-offline-status';
import {
  SettingsActionsSection,
  SettingsContentSurface,
  SettingsDataList,
  SettingsDataRow,
  SettingsNotice,
} from '../shared/settings-content';
import {
  SettingsBadge,
  SettingsEntityList,
  SettingsEntityMessage,
} from '../shared/settings-entity';
import { SettingsSearchableEntityPage } from '../shared/settings-entity-page';
import { SettingsPersonExpandableItem } from '../shared/settings-identity';
import * as styles from './styles.css';

interface GuestAccessTarget {
  type: 'doc' | 'folder';
  id: string;
  name: string;
}

export const CloudGuestsPanel = () => {
  const t = useI18n();
  const backendAvailable = useBackendAvailable();
  const offlineLabel = useSettingsOfflineIncompleteLabel();
  const membersService = useService(WorkspaceMembersService);
  const workspaceQuotaService = useService(WorkspaceQuotaService);
  const workspaceService = useService(WorkspaceService);
  const serverService = useService(WorkspaceServerService);
  const organizeService = useService(OrganizeService);
  const { getDocMeta } = useDocMetaHelper();
  const workspaceId = workspaceService.workspace.id;
  const folderTree = organizeService.folderTree;
  const rootFolder = folderTree.rootFolder;
  const folderChildren = useLiveData(rootFolder.sortedChildren$);

  const guestCount = useLiveData(workspaceQuotaService.quota.guestCount$);
  const guestLimit = useLiveData(workspaceQuotaService.quota.guestLimit$);

  const folderNameMap = useMemo(() => {
    const map = new Map<string, string>();
    const collect = (nodes: typeof folderChildren) => {
      for (const node of nodes) {
        if (node.type$.value === 'folder' && node.id) {
          map.set(node.id, node.name$.value || 'Unnamed Folder');
          collect(node.sortedChildren$.value);
        }
      }
    };
    collect(folderChildren);
    return map;
  }, [folderChildren]);

  const [guests, setGuests] = useState<Member[] | undefined>(undefined);
  const [guestTargets, setGuestTargets] = useState<
    Map<string, GuestAccessTarget[]>
  >(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedGuestId, setExpandedGuestId] = useState<string | null>(null);
  const [showExpired, setShowExpired] = useState(false);

  const fetchGuests = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    if (!backendAvailable) {
      setIsLoading(false);
      return;
    }
    try {
      const guestMembers = await membersService.fetchAllGuests();
      setGuests(guestMembers);

      const server = serverService.server;
      if (server && guestMembers.length > 0) {
        const targetMap = new Map<string, GuestAccessTarget[]>();
        await Promise.all(
          guestMembers.map(async guest => {
            const targets: GuestAccessTarget[] = [];
            try {
              const [docResult, folderResult] = await Promise.all([
                server.gql({
                  query: getUserDocGrantsQuery,
                  variables: { workspaceId, userId: guest.id },
                }),
                server.gql({
                  query: getUserFolderGrantsQuery,
                  variables: { workspaceId, userId: guest.id },
                }),
              ]);
              for (const grant of docResult.workspace?.userDocGrants ?? []) {
                const meta = getDocMeta(grant.docId);
                targets.push({
                  type: 'doc',
                  id: grant.docId,
                  name: meta?.title || 'Untitled',
                });
              }
              for (const grant of (folderResult as any).workspace
                ?.userFolderGrants ?? []) {
                targets.push({
                  type: 'folder',
                  id: grant.folderId,
                  name: folderNameMap.get(grant.folderId) || 'Unnamed Folder',
                });
              }
            } catch {
              // ignore per-guest grant failures
            }
            targetMap.set(guest.id, targets);
          })
        );
        setGuestTargets(targetMap);
      }
    } catch (err) {
      setError(UserFriendlyError.fromAny(err).message);
    } finally {
      setIsLoading(false);
    }
  }, [
    membersService,
    backendAvailable,
    serverService.server,
    workspaceId,
    getDocMeta,
    folderNameMap,
  ]);

  useEffect(() => {
    void fetchGuests();
    workspaceQuotaService.quota.revalidate();
  }, [fetchGuests, workspaceQuotaService]);

  const { activeGuests, expiredGuests } = useMemo(() => {
    if (!guests) return { activeGuests: [], expiredGuests: [] };
    const now = Date.now();
    const active: Member[] = [];
    const expired: Member[] = [];
    for (const guest of guests) {
      if (
        !guest.guestExpiresAt ||
        new Date(guest.guestExpiresAt).getTime() <= now
      ) {
        expired.push(guest);
      } else {
        active.push(guest);
      }
    }
    return { activeGuests: active, expiredGuests: expired };
  }, [guests]);

  const filteredActiveGuests = useMemo(() => {
    if (!searchQuery.trim()) return activeGuests;
    const q = searchQuery.toLowerCase();
    return activeGuests.filter(
      guest =>
        guest.name?.toLowerCase().includes(q) ||
        guest.email?.toLowerCase().includes(q)
    );
  }, [activeGuests, searchQuery]);

  const filteredExpiredGuests = useMemo(() => {
    if (!searchQuery.trim()) return expiredGuests;
    const q = searchQuery.toLowerCase();
    return expiredGuests.filter(
      guest =>
        guest.name?.toLowerCase().includes(q) ||
        guest.email?.toLowerCase().includes(q)
    );
  }, [expiredGuests, searchQuery]);

  const handleRemoveGuest = useCallback(
    async (guestId: string) => {
      try {
        await membersService.revokeMember(guestId);
        notify.success({
          title: t['com.streamlain.settings.workspace.guests.remove.success'](),
        });
        await fetchGuests();
        workspaceQuotaService.quota.revalidate();
        membersService.members.revalidate();
      } catch (err) {
        const error = UserFriendlyError.fromAny(err);
        notify.error({ title: error.message });
      }
    },
    [fetchGuests, membersService, t, workspaceQuotaService]
  );

  const handleCleanupExpired = useAsyncCallback(async () => {
    for (const guest of expiredGuests) {
      try {
        await membersService.revokeMember(guest.id);
      } catch {
        // continue
      }
    }
    notify.success({
      title: t['com.streamlain.settings.workspace.guests.cleanup.success'](),
    });
    await fetchGuests();
    workspaceQuotaService.quota.revalidate();
    membersService.members.revalidate();
  }, [expiredGuests, fetchGuests, membersService, t, workspaceQuotaService]);

  const quotaDisplay = useMemo(() => {
    if (guestCount === null || guestLimit === null) return null;
    if (guestLimit < 0) return null;
    return { count: guestCount, limit: guestLimit };
  }, [guestCount, guestLimit]);

  const quotaPercentage = useMemo(() => {
    if (!quotaDisplay || quotaDisplay.limit === 0) return 0;
    return Math.min((quotaDisplay.count / quotaDisplay.limit) * 100, 100);
  }, [quotaDisplay]);

  const title = useMemo(() => {
    if (guestCount !== null) {
      return `${t['com.streamlain.settings.workspace.guests.title']()} (${guestCount})`;
    }
    return t['com.streamlain.settings.workspace.guests.title']();
  }, [guestCount, t]);

  const hasItems =
    filteredActiveGuests.length > 0 || filteredExpiredGuests.length > 0;
  const showOfflineNotice = !backendAvailable;

  return (
    <SettingsSearchableEntityPage
      title={title}
      subtitle={t['com.streamlain.settings.workspace.guests.description']()}
      headerAction={<SettingsOfflineHeaderAction show={showOfflineNotice} />}
      searchValue={searchQuery}
      onSearchChange={setSearchQuery}
      searchPlaceholder={
        t['com.streamlain.settings.workspace.guests.search']?.() ??
        'Search guests...'
      }
      action={
        filteredExpiredGuests.length > 0 ? (
          <Button
            data-testid="settings-guests-cleanup-button"
            variant="error"
            onClick={() => {
              void handleCleanupExpired();
            }}
          >
            {t['com.streamlain.settings.workspace.guests.cleanup']()}
          </Button>
        ) : undefined
      }
      banner={
        quotaDisplay ? (
          <div className={styles.quotaSection}>
            <div className={styles.quotaText}>
              {t['com.streamlain.settings.workspace.guests.quota']({
                count: String(quotaDisplay.count),
                limit: String(quotaDisplay.limit),
              })}
            </div>
            <div className={styles.progressBarContainer}>
              <div
                className={clsx(
                  styles.progressBar,
                  quotaPercentage >= 90 && styles.progressBarWarning
                )}
                style={{ width: `${quotaPercentage}%` }}
              />
            </div>
          </div>
        ) : null
      }
      loading={isLoading && guests === undefined && !showOfflineNotice}
      hasItems={hasItems}
      loadingContent={
        <SettingsEntityMessage>
          <Loading size={20} />
          <span>{t['com.streamlain.settings.member.loading']()}</span>
        </SettingsEntityMessage>
      }
      emptyContent={
        error ? (
          <SettingsEntityMessage>
            {showOfflineNotice ? offlineLabel : error}
          </SettingsEntityMessage>
        ) : (
          <SettingsEntityMessage>
            {showOfflineNotice
              ? offlineLabel
              : searchQuery
                ? (t[
                    'com.streamlain.settings.workspace.guests.empty-search'
                  ]?.() ?? 'No guests found matching your search.')
                : t['com.streamlain.settings.workspace.guests.empty']()}
          </SettingsEntityMessage>
        )
      }
    >
      <>
        {filteredActiveGuests.length > 0 ? (
          <SettingsEntityList>
            {filteredActiveGuests.map(guest => (
              <GuestItem
                key={guest.id}
                guest={guest}
                accessTargets={guestTargets.get(guest.id) ?? []}
                expanded={expandedGuestId === guest.id}
                onToggle={() =>
                  setExpandedGuestId(prev =>
                    prev === guest.id ? null : guest.id
                  )
                }
                onRemove={handleRemoveGuest}
              />
            ))}
          </SettingsEntityList>
        ) : null}

        {filteredExpiredGuests.length > 0 ? (
          <>
            <Button
              variant="secondary"
              onClick={() => setShowExpired(prev => !prev)}
            >
              {`${t['com.streamlain.settings.workspace.guests.expired']()} (${filteredExpiredGuests.length})`}
            </Button>
            {showExpired ? (
              <SettingsEntityList>
                {filteredExpiredGuests.map(guest => (
                  <GuestItem
                    key={guest.id}
                    guest={guest}
                    accessTargets={guestTargets.get(guest.id) ?? []}
                    expanded={expandedGuestId === guest.id}
                    onToggle={() =>
                      setExpandedGuestId(prev =>
                        prev === guest.id ? null : guest.id
                      )
                    }
                    onRemove={handleRemoveGuest}
                    expired
                  />
                ))}
              </SettingsEntityList>
            ) : null}
          </>
        ) : null}
      </>
    </SettingsSearchableEntityPage>
  );
};

const GuestItem = ({
  guest,
  accessTargets,
  expanded,
  onToggle,
  onRemove,
  expired,
}: {
  guest: Member;
  accessTargets: GuestAccessTarget[];
  expanded: boolean;
  onToggle: () => void;
  onRemove: (id: string) => Promise<void>;
  expired?: boolean;
}) => {
  const t = useI18n();
  const { openConfirmModal } = useConfirmModal();

  const roleName = useMemo(() => {
    switch (guest.permission) {
      case Permission.Editor:
        return t['com.streamlain.share-menu.invite-editor.guest-role.editor']();
      case Permission.Reader:
      default:
        return t['com.streamlain.share-menu.invite-editor.guest-role.reader']();
    }
  }, [guest.permission, t]);

  const expiryDisplay = useMemo(() => {
    if (!guest.guestExpiresAt) return null;
    const expiryDate = new Date(guest.guestExpiresAt);
    const now = Date.now();
    const diff = expiryDate.getTime() - now;

    if (diff <= 0) {
      return {
        text: t['com.streamlain.settings.workspace.guests.status.expired'](),
        isExpired: true,
      };
    }

    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours < 24) {
      return {
        text: t['com.streamlain.settings.workspace.guests.countdown.hours']({
          hours: String(hours),
        }),
        isExpired: false,
      };
    }

    const days = Math.floor(hours / 24);
    return {
      text: t['com.streamlain.settings.workspace.guests.countdown.days']({
        days: String(days),
      }),
      isExpired: false,
    };
  }, [guest.guestExpiresAt, t]);

  const handleRemoveClick = useCallback(() => {
    openConfirmModal({
      title:
        t['com.streamlain.settings.workspace.guests.remove.confirm.title'](),
      description: t[
        'com.streamlain.settings.workspace.guests.remove.confirm.description'
      ]({
        name: guest.name || guest.email || guest.id,
      }),
      confirmText:
        t['com.streamlain.settings.workspace.guests.remove.confirm.confirm'](),
      cancelText: t['Cancel'](),
      confirmButtonOptions: { variant: 'error' },
      onConfirm: () => onRemove(guest.id),
    });
  }, [guest, onRemove, openConfirmModal, t]);

  return (
    <SettingsPersonExpandableItem
      dataTestId={`settings-guest-item-${guest.id}`}
      expanded={expanded}
      onToggle={onToggle}
      person={guest}
      fallbackName="Guest"
      badges={
        <>
          <SettingsBadge>{roleName}</SettingsBadge>
          {expiryDisplay ? (
            <SettingsBadge variant={expiryDisplay.isExpired ? 'error' : 'soft'}>
              {expiryDisplay.text}
            </SettingsBadge>
          ) : null}
        </>
      }
      showEmailMeta={Boolean(guest.name && guest.email)}
    >
      <SettingsContentSurface>
        {accessTargets.length > 0 ? (
          <SettingsDataList>
            {accessTargets.map(target => (
              <SettingsDataRow
                key={`${target.type}-${target.id}`}
                label={
                  target.type === 'doc'
                    ? (t['Documents']?.() ?? 'Document')
                    : (t['Folders']?.() ?? 'Folder')
                }
                value={target.name}
              />
            ))}
          </SettingsDataList>
        ) : (
          <SettingsNotice
            title={
              t[
                'com.streamlain.settings.workspace.guests.no-access-targets.title'
              ]?.() ?? 'No direct access entries'
            }
          >
            {t[
              'com.streamlain.settings.workspace.guests.no-access-targets.description'
            ]?.() ??
              'No document or folder grants are currently listed for this guest.'}
          </SettingsNotice>
        )}

        <SettingsActionsSection
          title={
            t['com.streamlain.settings.workspace.guests.actions.title']?.() ??
            'Guest actions'
          }
        >
          <Button
            data-testid={`settings-guest-remove-${guest.id}`}
            variant="error"
            onClick={handleRemoveClick}
            style={{ opacity: expired ? 0.8 : 1 }}
          >
            {t['com.streamlain.settings.workspace.guests.remove.action']?.() ??
              'Remove guest'}
          </Button>
        </SettingsActionsSection>
      </SettingsContentSurface>
    </SettingsPersonExpandableItem>
  );
};
